import React from 'react';
import {useSelector} from 'react-redux';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faDownload, faFileExcel, faEye} from '@fortawesome/free-solid-svg-icons';
import {formatDateTime} from '../utils/fileUtils';
import {horizonShortLabel} from '../utils/horizonLabels';
import {selectBlurSensitive} from '../../../../../slices/preferencesSlice';
import {exportStatusDisplay} from '../utils/exportStatus';

/**
 * Individual export item component
 */
export const ExportItem = ({item, resourceName, onDownload, onView}) => {
  const blurSensitive = useSelector(selectBlurSensitive);
  const start = item.start_date ? String(item.start_date).slice(0, 10) : '';
  const end = item.end_date ? String(item.end_date).slice(0, 10) : '';
  const status = (item.status || item.state || '').toString();
  const created = item.created_at || item.created || '';
  const records = item.record_count || item.records || 0;
  const exportType = item.export_type || 'historical_forecast';
  const fileFormat = item.file_type || item.format || 'CSV';
  const userEmail = item.user_email || null;
  const hasHorizon = item.horizon !== null && item.horizon !== undefined;
  const horizonBadge = hasHorizon ? horizonShortLabel(item.horizon) : null;
  const statusDisplay = exportStatusDisplay(status);

  // Check if file is Excel/XLSX based on format
  const isExcelFile = fileFormat && (
    fileFormat.toUpperCase() === 'XLSX' ||
    fileFormat.toUpperCase() === 'EXCEL' ||
    fileFormat.toUpperCase().includes('XLS')
  );

  // Format export type for display
  const exportTypeDisplay = exportType === 'historical_forecast'
    ? 'Historical Forecast'
    : exportType === 'market'
      ? 'Market Report'
      : exportType === 'raw_measurement'
        ? 'Raw Measurement Data'
        : exportType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  return (
    <div className="relative p-3 sm:p-3 rounded-lg border border-gray-200/70 dark:border-gray-600/70 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm transition-all bg-surface dark:bg-gray-870">
      {/* Mobile: Vertical layout */}
      <div className="sm:hidden space-y-2">
        <div className="flex items-start gap-2">
          <div className="flex-shrink-0 mt-0.5">
            {isExcelFile ? (
              <FontAwesomeIcon icon={faFileExcel} className="w-5 h-5 text-green-600 dark:text-green-400" />
            ) : (
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm text-gray-900 dark:text-gray-100 break-words">
              {resourceName}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${
                isExcelFile
                  ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200'
                  : 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200'
              }`}>
                {fileFormat}
              </span>
              {horizonBadge && (
                <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-200">
                  {horizonBadge}
                </span>
              )}
              <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${statusDisplay.className}`}>
                {statusDisplay.label}
              </span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {start && end && <div>{start} → {end}</div>}
              {records > 0 && <div>{records} records</div>}
            </div>
          </div>
        </div>
        <div className="pl-7 flex gap-2">
          {(exportType === 'market' || exportType === 'market_report' || exportType?.includes('market')) && onView && (
            <button
              onClick={() => onView(item)}
              disabled={status !== 'completed'}
              className="flex-1 py-1.5 px-3 text-xs font-medium rounded-md border border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              View
            </button>
          )}
          <button
            onClick={() => onDownload(item, resourceName)}
            disabled={status !== 'completed'}
            className="flex-1 py-1.5 px-3 text-xs font-medium rounded-md border border-brand-200 dark:border-brand-700 bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 hover:bg-brand-100 dark:hover:bg-brand-900/50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Download
          </button>
        </div>
      </div>

      {/* Desktop: Horizontal layout */}
      <div className="hidden sm:flex items-start gap-4">
        <div className="flex-shrink-0 mt-1">
          {isExcelFile ? (
            <FontAwesomeIcon icon={faFileExcel} className="w-6 h-6 text-green-600 dark:text-green-400" />
          ) : (
            <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
              {resourceName}
            </span>
            <span className={`px-1.5 py-0.5 text-xs font-medium rounded flex-shrink-0 ${
              isExcelFile
                ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200'
                : 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200'
            }`}>
              {fileFormat}
            </span>
            {horizonBadge && (
              <span className="px-1.5 py-0.5 text-xs font-medium rounded flex-shrink-0 bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-200">
                {horizonBadge}
              </span>
            )}
            <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
              {exportTypeDisplay}
            </span>
          </div>

          <div className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
            <div className="flex items-center gap-1.5">
              <span className="text-gray-400 dark:text-gray-500">Period:</span>
              <span className="truncate">{start}{start && end ? ' → ' : ''}{end}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-gray-400 dark:text-gray-500">Requested:</span>
              <span className="truncate">{formatDateTime(created)}</span>
              {records > 0 && <span className="flex-shrink-0">• {records} records</span>}
            </div>
            {userEmail && (
              <div className="flex items-center gap-1.5">
                <span className="text-gray-400 dark:text-gray-500">User:</span>
                <span className={`truncate ${blurSensitive ? 'blur-sm select-none' : ''}`}>{userEmail}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`px-2 py-1 rounded-md text-xs font-medium whitespace-nowrap ${statusDisplay.className}`}>
            {statusDisplay.label}
          </span>

          {(exportType === 'market' || exportType === 'market_report' || exportType?.includes('market')) && onView ? (
            <button
              onClick={() => onView(item)}
              disabled={status !== 'completed'}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="View Report"
            >
              <FontAwesomeIcon icon={faEye} className="w-4 h-4"/>
            </button>
          ) : (
            <div className="w-9" />
          )}

          <button
            onClick={() => onDownload(item, resourceName)}
            disabled={status !== 'completed'}
            className="p-2 text-gray-600 dark:text-gray-400 hover:text-brand-600 dark:hover:text-brand-400 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Download"
          >
            <FontAwesomeIcon icon={faDownload} className="w-4 h-4"/>
          </button>
        </div>
      </div>
    </div>
  );
};

